import { ArrowBack } from "@mui/icons-material";
import { IconButton, Paper, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { tracker } from "@openreplay/tracker";
import { useGetDataObjectByIdQuery } from "@store/api/dataApi";
import { getResourceErrorStatus } from "@store/api/types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import ResourceAccessErrorPanel from "@components/ResourceAccessErrorPanel";

import { DATA_EXPLORER_ROUTE, DATA_TREE_SEGMENT } from "@helpers/constants";
import { DATA_EXPLORER_EVENTS } from "@helpers/events";

import DataColumn from "../components/DataColumn";
import DownloadButton, {
  type SelectionKind,
} from "../components/DownloadButton";
import Loading from "../components/Loading";
import SiteTitle from "../components/SiteTitle";
import type { ClientFile } from "../helpers/utils";

const Page = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateRows: "auto 1fr",
  height: "100%",
  gap: theme.spacing(2),
  padding: theme.spacing(4),
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(4, 9),
  },
}));

const Header = styled("header")<{ canGoBack?: boolean }>(
  ({ theme, canGoBack }) => ({
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    [theme.breakpoints.up("md")]: {
      marginLeft: canGoBack ? `calc(-44px - ${theme.spacing(2)})` : 0,
    },
  }),
);

/** An entry picked from a column's listing, and the path it was listed at. */
export type Selection = { path: string; file: ClientFile };

const pathKey = (path: string) => path.split("/").filter(Boolean).join("/");

/**
 * `selection` is set only once the parent listing loads and may still be the
 * previous one, so it counts only if it was listed at the current path. The
 * name alone isn't enough: "a/x" and "b/x" can be a file and a folder.
 */
export function getSelectionKind(
  currentPath: string,
  selection?: Selection,
): SelectionKind {
  if (!pathKey(currentPath)) {
    return "folder";
  }
  if (!selection || pathKey(selection.path) !== pathKey(currentPath)) {
    return "pending";
  }
  return selection.file.kind === "directory" ? "folder" : "file";
}

const FileBrowser = styled(Paper)(() => ({
  display: "grid",
  gridAutoFlow: "column",
  height: "100%",
  overflowX: "scroll",
  justifyContent: "start",
  "&.empty": {
    justifyContent: "stretch",
  },
}));

export function useCurrentPath(props?: { prefixWithDot: boolean }) {
  const location = useLocation();
  const currentFilepathIndex = location.pathname.indexOf(DATA_TREE_SEGMENT);
  const encodedPath =
    currentFilepathIndex > 0
      ? location.pathname.slice(currentFilepathIndex + DATA_TREE_SEGMENT.length)
      : "";
  // A trailing slash would open an extra column for the same folder, with
  // nothing selected in the column before it.
  const path = encodedPath
    .replace(/\/+$/, "")
    .split("/")
    .map((segment) => (segment ? decodeURIComponent(segment) : segment))
    .join("/");
  return props?.prefixWithDot ? `.${path}` : path;
}

export const DataContext = React.createContext<{
  file?: ClientFile;
  setFile?: (path: string, file: ClientFile) => void;
}>({});

export const Data = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const canGoBack = location.state?.canGoBack;
  const { dataId } = useParams<{ dataId: string }>();
  const [selection, setSelection] = useState<Selection>();
  const setFile = useCallback(
    (path: string, file: ClientFile) => setSelection({ path, file }),
    [],
  );
  // Don't show download button until we confirm the folder has entries
  // because an empty folder has nothing to download and the server would
  // answer with an error page that replaces the app.
  const [hasEntries, setHasEntries] = useState<Record<string, boolean>>({});
  const onListed = useCallback(
    (path: string, folderHasEntries: boolean) =>
      setHasEntries((current) => ({ ...current, [path]: folderHasEntries })),
    [],
  );

  const currentPath = useCurrentPath();
  // Nothing updates `selection` at the top level, so after going back there
  // it still holds the last pick, and a stale file would turn the only
  // column into its preview.
  const file =
    selection && pathKey(selection.path) === pathKey(currentPath)
      ? selection.file
      : undefined;
  const columns = currentPath
    .split("/")
    .reduce<
      string[]
    >((columns, segment, index) => columns.concat(index > 0 ? [[columns[index - 1], segment].join("/")] : [segment]), []);

  const fileBrowser = useRef<HTMLDivElement>(null);

  const {
    data: currentData,
    isLoading: loading,
    error,
  } = useGetDataObjectByIdQuery({ id: dataId ?? "" }, { skip: !dataId });

  useEffect(() => {
    const currentList = document.querySelector(`.column:last-child`);
    if (currentList) {
      currentList.scrollIntoView();
    }
  }, [columns]);

  // Check the error before the loading/no-data guard: on a 403 there's no data
  // and we're not loading, so the guard below would otherwise spin forever.
  if (error) {
    return (
      <Page>
        <ResourceAccessErrorPanel
          status={getResourceErrorStatus(error)}
          resourceType="data"
          resourceIdentifier={dataId}
          backTo={DATA_EXPLORER_ROUTE}
          backLabel="Back to data explorer"
        />
      </Page>
    );
  }

  if (loading || !currentData) {
    return <Loading />;
  }

  const kind = getSelectionKind(currentPath, selection);
  const showDownload =
    kind !== "folder" || hasEntries[columns[columns.length - 1]];

  return (
    <DataContext.Provider value={{ file, setFile }}>
      <Page>
        <Header canGoBack={canGoBack}>
          {canGoBack && (
            <IconButton
              onClick={() => {
                tracker.event(DATA_EXPLORER_EVENTS.DETAIL_BACK_CLICKED, dataId);
                navigate(-1);
              }}
            >
              <ArrowBack />
            </IconButton>
          )}
          <SiteTitle
            subtitle={
              <Typography variant="h3">
                {currentData.name ?? currentData.id}
              </Typography>
            }
          />
          {showDownload && (
            <DownloadButton
              dataId={dataId ?? ""}
              path={pathKey(currentPath)}
              kind={kind}
            />
          )}
        </Header>
        <FileBrowser ref={fileBrowser}>
          {columns.map((path, index) => (
            <DataColumn
              key={path}
              dataId={dataId ?? ""}
              path={path}
              index={columns.length - 1 - index}
              activeSegment={
                columns[index + 1] && columns[index + 1].split(`${path}/`)[1]
              }
              onListed={onListed}
            />
          ))}
        </FileBrowser>
      </Page>
    </DataContext.Provider>
  );
};

export default Data;
